import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'
import {
  getActivePublicTournaments,
  getCountries,
  getCitiesByCountry,
  enrollPublicParticipant,
  enrollPublicParticipantBulk,
} from '../services/publicService'
import type { PublicTournament, CountryItem, CityItem } from '../types/public'
import { LocationFieldButton, SelectPanel } from './SearchableSelect'

type GameType = 'football' | 'formula1'

export type PlayPublicEnrollResult = {
  tournamentIds: string[]
  countryCode: string
  city: string
}

interface PlayPublicModalProps {
  onClose: () => void
  onSuccess: (tournamentId: string, gameType?: GameType, extras?: PlayPublicEnrollResult) => void
  /** When set, only this game type can be selected (user already has the other). */
  onlyGameType?: GameType
  initialCountryCode?: string
  initialCity?: string
}

const PlayPublicModal: React.FC<PlayPublicModalProps> = ({
  onClose,
  onSuccess,
  onlyGameType,
  initialCountryCode,
  initialCity,
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<1 | 2>(onlyGameType ? 2 : 1)
  const [gameType, setGameType] = useState<GameType>(onlyGameType ?? 'football')
  const [tournaments, setTournaments] = useState<PublicTournament[]>([])
  const [countries, setCountries] = useState<CountryItem[]>([])
  const [cities, setCities] = useState<CityItem[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [countryCode, setCountryCode] = useState(() => (initialCountryCode ?? '').trim().toUpperCase())
  const [city, setCity] = useState(() => (initialCity ?? '').trim())
  const [loading, setLoading] = useState(false)
  const [loadingTournaments, setLoadingTournaments] = useState(true)
  const [openPicker, setOpenPicker] = useState<'country' | 'city' | null>(null)
  const [pickerQuery, setPickerQuery] = useState('')

  useEffect(() => {
    if (onlyGameType) {
      setGameType(onlyGameType)
      setStep(2)
    }
  }, [onlyGameType])

  useEffect(() => {
    let cancelled = false
    setLoadingTournaments(true)
    getActivePublicTournaments(gameType)
      .then((tList) => {
        if (cancelled) return
        setTournaments(tList)
      })
      .catch(() => {
        if (!cancelled) toast.error(t('playPublic.loadError'))
        if (!cancelled) setTournaments([])
      })
      .finally(() => {
        if (!cancelled) setLoadingTournaments(false)
      })
    return () => { cancelled = true }
  }, [gameType, t])

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]))
  }, [])

  useEffect(() => {
    if (!countryCode.trim()) {
      setCities([])
      return
    }
    let cancelled = false
    setLoadingCities(true)
    getCitiesByCountry(countryCode)
      .then((list) => {
        if (cancelled) return
        setCities(list)
        setCity((current) => {
          if (current && list.some((item) => item.cityName === current)) return current
          const seeded = (initialCity ?? '').trim()
          const seededCountry = (initialCountryCode ?? '').trim().toUpperCase()
          if (
            seeded &&
            countryCode.trim().toUpperCase() === seededCountry &&
            list.some((item) => item.cityName === seeded)
          ) {
            return seeded
          }
          return ''
        })
      })
      .catch(() => {
        if (!cancelled) setCities([])
      })
      .finally(() => {
        if (!cancelled) setLoadingCities(false)
      })
    return () => { cancelled = true }
  }, [countryCode, initialCity, initialCountryCode])

  const handleEnroll = async () => {
    if (!countryCode.trim()) {
      toast.error(t('playPublic.countryRequired'))
      return
    }
    if (!city.trim()) {
      toast.error(t('playPublic.cityRequired'))
      return
    }
    const leagueIds = tournaments.map((t) => t.id)
    if (leagueIds.length === 0) {
      toast.error(t('playPublic.noTournaments'))
      return
    }
    setLoading(true)
    try {
      if (leagueIds.length > 1) {
        await enrollPublicParticipantBulk({
          tournamentIds: leagueIds,
          countryCode: countryCode.trim().toUpperCase(),
          city: city.trim(),
        })
      } else {
        await enrollPublicParticipant({
          publicTournamentId: leagueIds[0],
          countryCode: countryCode.trim().toUpperCase(),
          city: city.trim(),
        })
      }
      toast.success(t('playPublic.enrolled'))
      onSuccess(leagueIds[0], gameType, {
        tournamentIds: leagueIds,
        countryCode: countryCode.trim().toUpperCase(),
        city: city.trim(),
      })
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('playPublic.enrollFailed'))
    } finally {
      setLoading(false)
    }
  }

  const countryItems = countries.map((c) => ({ value: c.code, label: c.name }))
  const cityItems = cities.map((c) => ({ value: c.cityName, label: c.cityName }))
  const selectedCountryLabel = countries.find((c) => c.code === countryCode)?.name || ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="glass-card rounded-2xl max-w-md w-full h-[min(36rem,calc(100vh-2rem))] max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {openPicker && step === 2 ? (
          <SelectPanel
            title={openPicker === 'country' ? t('playPublic.country') : t('playPublic.city')}
            items={openPicker === 'country' ? countryItems : cityItems}
            value={openPicker === 'country' ? countryCode : city}
            query={pickerQuery}
            onQueryChange={setPickerQuery}
            placeholder={
              openPicker === 'country' ? t('playPublic.selectCountry') : t('playPublic.selectCity')
            }
            emptyLabel={
              openPicker === 'city' && countryCode && cities.length === 0
                ? t('playPublic.noCitiesForCountry')
                : undefined
            }
            onBack={() => {
              setOpenPicker(null)
              setPickerQuery('')
            }}
            onSelect={(value) => {
              if (openPicker === 'country') {
                setCountryCode(value)
                setCity('')
              } else {
                setCity(value)
              }
              setOpenPicker(null)
              setPickerQuery('')
            }}
          />
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
        <div className="flex justify-between items-center px-6 pt-6 pb-4 shrink-0">
          <h2 className="font-display text-xl font-bold text-foreground">{t('playPublic.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="flex-1 overflow-y-auto px-6">
            <p className="text-sm text-muted-foreground mb-4">{t('playPublic.step1Desc')}</p>
            <p className="text-sm font-medium text-foreground mb-2">{t('createRoom.gameType')}</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {(onlyGameType
                ? ([onlyGameType] as GameType[])
                : (['football', 'formula1'] as GameType[])
              ).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setGameType(type)}
                  className={`rounded-xl py-3 flex flex-col items-center gap-1 text-sm font-medium transition-all border ${
                    gameType === type
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-muted/20 bg-muted/5 text-muted-foreground hover:border-muted/40'
                  }`}
                >
                  <span className="text-xl">
                    {type === 'football' ? '⚽' : '🏎️'}
                  </span>
                  <span>
                    {type === 'football' ? t('room.gameFootball') : t('room.gameFormula1')}
                  </span>
                </button>
              ))}
            </div>
            </div>
            <div className="flex gap-2 justify-end px-6 pb-6 pt-3 shrink-0">
              <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-xl">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium"
              >
                {t('playPublic.next')}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex-1 overflow-y-auto px-6">
            <p className="text-sm text-muted-foreground mb-4">{t('playPublic.step2Desc')}</p>
            <div className="space-y-4 mb-6">
              <LocationFieldButton
                label={`${t('playPublic.country')} *`}
                value={selectedCountryLabel}
                placeholder={t('playPublic.selectCountry')}
                onOpen={() => {
                  setPickerQuery('')
                  setOpenPicker('country')
                }}
              />
              <div>
                {loadingCities ? (
                  <p className="text-sm text-muted-foreground py-2">{t('common.loading')}</p>
                ) : (
                  <LocationFieldButton
                    label={`${t('playPublic.city')} *`}
                    value={city}
                    placeholder={countryCode ? t('playPublic.selectCity') : t('playPublic.selectCountryFirst')}
                    disabled={!countryCode}
                    onOpen={() => {
                      setPickerQuery('')
                      setOpenPicker('city')
                    }}
                  />
                )}
                {countryCode && cities.length === 0 && !loadingCities && (
                  <p className="text-xs text-muted-foreground mt-1">{t('playPublic.noCitiesForCountry')}</p>
                )}
              </div>
            </div>
            </div>
            <div className="flex gap-2 justify-end px-6 pb-6 pt-3 shrink-0">
              <button
                type="button"
                onClick={() => (onlyGameType ? onClose() : setStep(1))}
                className="btn-secondary px-4 py-2 rounded-xl"
              >
                {t('playPublic.back')}
              </button>
              <button
                type="button"
                onClick={handleEnroll}
                disabled={!countryCode.trim() || !city.trim() || loading || loadingTournaments || tournaments.length === 0}
                className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-50"
              >
                {loading ? t('common.loading') : t('playPublic.enroll')}
              </button>
            </div>
          </>
        )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlayPublicModal
